import {
  Ban,
  DollarSign,
  Eye,
  Layers,
  type LucideIcon,
  Paintbrush,
  Pencil,
  Plus,
  RotateCcw,
  Upload,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { DataTable, FilterBar, PageHeader } from "../../components/ui/patrones";
import { Aviso, Boton, Campo, Etiqueta, Interruptor, Modal } from "../../components/ui/primitivos";
import {
  actualizarProducto,
  borrarProducto,
  crearProducto,
  fetchCamposImportacionProductos,
  fetchProductos,
} from "../../lib/consola-api";
import { type Orden, ordenarLista, siguienteOrden } from "../../lib/orden";
import { useSession } from "../../lib/SessionContext";
import { useDatos } from "../../lib/useDatos";
import { ImportarProductos } from "./ImportarProductos";
import { estadoDeProducto } from "./producto";

/**
 * Productos y sus combinaciones (ADR 0010) — porteada de `productos` de
 * `imprenta`.
 *
 * El listado muestra cuántos tamaños y bases tiene armados cada producto: sin
 * al menos uno de cada, no se le puede poner precio y por lo tanto no se puede
 * cotizar. Es la información que decide si el producto está listo o le falta
 * configuración.
 */
export function ProductosPagina() {
  const { can } = useSession();
  const navegar = useNavigate();
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [orden, setOrden] = useState<Orden | null>(null);
  const productos = useDatos(() => fetchProductos(mostrarInactivos), String(mostrarInactivos));
  const camposImportacion = useDatos(() =>
    can("productos", "import") ? fetchCamposImportacionProductos() : Promise.resolve([]),
  );
  const [creando, setCreando] = useState(false);
  const [importando, setImportando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const puedeCrear = can("productos", "create");
  const puedeEditar = can("productos", "update");
  const puedeBorrar = can("productos", "delete");
  const puedeImportar = can("productos", "import");

  const termino = busqueda.trim().toLowerCase();
  const filtrados = (productos.datos ?? []).filter(
    (p) => !termino || p.nombre.toLowerCase().includes(termino),
  );

  const listado = ordenarLista(filtrados, orden, (p, campo) => {
    switch (campo) {
      case "nombre":
        return p.nombre;
      case "tamanos":
        return p.tamanos;
      case "bases":
        return p.bases;
      case "precios":
        return p.precios;
      case "estado":
        return p.activo ? "Activo" : "Inactivo";
      default:
        return null;
    }
  });

  async function accion(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      await productos.recargar();
    } catch (err) {
      setError(err instanceof Error ? err.message : "La acción falló");
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        titulo="Productos"
        subtitulo="Qué se imprime, y con qué tamaños de logo y bases"
        acciones={
          <>
            {puedeImportar ? (
              <Boton variante="secundario" onClick={() => setImportando(true)}>
                <Upload className="mr-2 inline h-4 w-4" aria-hidden="true" />
                Importar planilla
              </Boton>
            ) : null}
            {puedeCrear ? (
              <Boton onClick={() => setCreando(true)}>
                <Plus className="mr-2 inline h-4 w-4" aria-hidden="true" />
                Nuevo Producto
              </Boton>
            ) : null}
          </>
        }
      />

      {error ? <Aviso>{error}</Aviso> : null}
      {productos.error ? <Aviso>{productos.error}</Aviso> : null}

      <FilterBar
        filtrosActivos={termino ? 1 : 0}
        alLimpiar={() => setBusqueda("")}
        busqueda={
          <input
            aria-label="Buscar productos"
            placeholder="Buscar por nombre..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="w-full rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-neutral-900 text-sm"
          />
        }
      >
        <div className="flex items-center gap-2 text-neutral-700 text-sm">
          <Interruptor
            aria-label="Mostrar inactivos"
            checked={mostrarInactivos}
            onCheckedChange={setMostrarInactivos}
          />
          <span aria-hidden="true">Mostrar inactivos</span>
        </div>
      </FilterBar>

      <DataTable
        datos={listado}
        orden={orden}
        alOrdenar={(campo) => setOrden((o) => siguienteOrden(o, campo))}
        clave={(p) => p.id}
        cargando={productos.cargando}
        mensajeVacio="No hay productos"
        // La fila entera abre la ficha, como allá: el nombre ya no es un botón
        // suelto en una tabla donde todo lo demás es un número.
        alClickearFila={(p) => navegar(`/productos/${p.id}`)}
        columnas={[
          {
            clave: "nombre",
            ordenarPor: "nombre",
            encabezado: "Producto",
            className: "font-medium",
            // Los inactivos se atenúan pero no se ocultan: el borrado es lógico.
            render: (p) => <span className={p.activo ? "" : "opacity-60"}>{p.nombre}</span>,
          },
          {
            clave: "tamanos",
            ordenarPor: "tamanos",
            encabezado: "Tamaños",
            className: "text-center",
            // Contador con su ícono, como allá: tres números pelados en fila
            // se leen como una sola cifra partida en tres.
            render: (p) => <Contador icono={Layers} valor={p.tamanos} tono="info" />,
          },
          {
            clave: "bases",
            ordenarPor: "bases",
            encabezado: "Bases",
            className: "text-center",
            render: (p) => <Contador icono={Paintbrush} valor={p.bases} tono="ok" />,
          },
          {
            clave: "precios",
            ordenarPor: "precios",
            encabezado: "Precios",
            className: "text-center",
            // Los VIGENTES hoy: responde "¿se puede cotizar esto ahora?".
            render: (p) => <Contador icono={DollarSign} valor={p.precios} tono="aviso" />,
          },
          {
            clave: "estado",
            ordenarPor: "estado",
            encabezado: "Estado",
            // Sin tamaños, sin bases o sin precio no hay nada que cotizar.
            render: (p) => {
              const estado = estadoDeProducto(p);
              return <Etiqueta tono={estado.tono}>{estado.texto}</Etiqueta>;
            },
          },
          {
            clave: "acciones",
            encabezado: "",
            className: "text-right",
            render: (p) => (
              <div className="flex justify-end gap-2">
                {/* Abrir la ficha es NAVEGAR, no montar un modal (ADR 0064).
                    Con ruta propia el producto se comparte y se abre en otra
                    pestaña — y la pestaña interna viaja en la URL. */}
                {/* Sin texto (ADR 0091): el nombre va en `aria-label` y `title`. */}
                <Boton
                  variante="fantasma"
                  tamano="icono"
                  aria-label={`${puedeEditar ? "Configurar" : "Ver"} ${p.nombre}`}
                  title={puedeEditar ? "Configurar" : "Ver"}
                  onClick={() => navegar(`/productos/${p.id}`)}
                >
                  {puedeEditar ? (
                    <Pencil className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Eye className="h-4 w-4" aria-hidden="true" />
                  )}
                </Boton>
                {puedeBorrar && p.activo ? (
                  <Boton
                    variante="fantasma"
                    tamano="icono"
                    className="text-red-600 hover:bg-red-50 hover:text-red-700"
                    aria-label={`Desactivar ${p.nombre}`}
                    title="Desactivar"
                    onClick={() => accion(() => borrarProducto(p.id))}
                  >
                    <Ban className="h-4 w-4" aria-hidden="true" />
                  </Boton>
                ) : null}
                {puedeEditar && !p.activo ? (
                  <Boton
                    variante="fantasma"
                    tamano="icono"
                    aria-label={`Activar ${p.nombre}`}
                    title="Activar"
                    onClick={() => accion(() => actualizarProducto(p.id, { activo: true }))}
                  >
                    <RotateCcw className="h-4 w-4" aria-hidden="true" />
                  </Boton>
                ) : null}
              </div>
            ),
          },
        ]}
      />

      {creando ? (
        <ModalNuevo
          onCerrar={() => setCreando(false)}
          onCreado={async () => {
            setCreando(false);
            await productos.recargar();
          }}
        />
      ) : null}

      {importando ? (
        <ImportarProductos
          campos={camposImportacion.datos ?? []}
          onCerrar={() => setImportando(false)}
          onImportado={() => productos.recargar()}
        />
      ) : null}
    </div>
  );
}

function ModalNuevo({
  onCerrar,
  onCreado,
}: {
  onCerrar: () => void;
  onCreado: () => Promise<void>;
}) {
  const [nombre, setNombre] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  return (
    <Modal abierto onCerrar={onCerrar} titulo="Nuevo producto">
      <div className="space-y-4">
        {error ? <Aviso>{error}</Aviso> : null}
        <Campo
          etiqueta="Nombre"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="Ej: Taza cerámica"
          ayuda="Los tamaños y bases se configuran después de crearlo"
        />
        <div className="flex justify-end gap-2">
          <Boton variante="secundario" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton
            disabled={guardando || nombre.trim() === ""}
            onClick={async () => {
              setGuardando(true);
              setError(null);
              try {
                await crearProducto(nombre.trim());
                await onCreado();
              } catch (err) {
                setError(err instanceof Error ? err.message : "No se pudo crear");
              } finally {
                setGuardando(false);
              }
            }}
          >
            {guardando ? "Creando..." : "Crear"}
          </Boton>
        </div>
      </div>
    </Modal>
  );
}

/** Un contador con su ícono — los badges de `ProductosPage`. */
function Contador({
  icono: Icono,
  valor,
  tono,
}: {
  icono: LucideIcon;
  valor: number;
  tono: "ok" | "aviso" | "info";
}) {
  const estilos = {
    ok: "bg-emerald-100 text-emerald-800",
    aviso: "bg-amber-100 text-amber-800",
    info: "bg-sky-100 text-sky-800",
  } as const;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-2 py-0.5 font-medium text-xs ${estilos[tono]}`}
    >
      <Icono aria-hidden="true" className="h-3 w-3" />
      {valor}
    </span>
  );
}
